import { useEffect, useState } from 'react';
import { Button, Col, DatePicker, DatePickerProps, Form, Input, Row, Select, Switch } from 'antd';
import { FormProps } from 'antd/es/form/Form';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import TextArea from 'antd/es/input/TextArea';

// services
import NotificationService from 'core/services/notification';
import { onGetAllCountries } from 'modules/countries/services/countries';
import { onGetUser, onUpdateUser, UpdateUserPayload } from 'modules/users/services/users';
import { onGetAllLanguages } from 'modules/languages/services/languages';

// components
import BackButton from 'components/backButton/backButton';
import Icon from 'utils/icon';
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import LoadingComponent from 'components/loadingComponent/loadingComponent';

// models
import { User, UserProfileStatus } from 'modules/users/models/user';
import { useTranslation } from 'react-i18next';

type FieldType = {
	name_ar: string;
	name_en: string;
	national_id: number;
	date_of_birth: string;
	phone_country_code: string;
	phone_number: string;
	address: string;
	locale: string;
	country_id: number;
	profile_status: 'Active' | 'Blocked';
	profile_status_reason?: string;
	email?: 'string';
};

function UpdateUserPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [isCountriesLoading, setIsCountriesLoading] = useState(true);
	const [isLanguagesLoading, setIsLanguagesLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [user, setUser] = useState<User>();
	const [userStatus, setUserStatus] = useState<UserProfileStatus>();
	const [countries, setCountries] = useState<{ label: string; value: number }[]>([]);
	const [languages, setLanguages] = useState<{ label: string; value: string }[]>([]);
	const [updateForm] = Form.useForm();

	useEffect(() => {
		getUser();
		getCountries();
		getLanguages();
	}, []);

	const getUser = async () => {
		try {
			const response = await onGetUser(id as string);

			setUser(response?.data?.data);
			updateForm.setFieldsValue({
				...response?.data?.data,
				country_id: response?.data?.data?.country?.id,
				date_of_birth: dayjs(response?.data?.data?.date_of_birth, 'YYYY-MM-DD hh:mm:ss'),
				name_ar: response?.data?.data?.name?.ar,
				name_en: response?.data?.data?.name?.en,
			});

			setUserStatus(response?.data?.data?.profile_status);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('USERS.FETCH_CLIENT_ERROR'));
			navigate('/users');
		}
	};

	const getCountries = async () => {
		try {
			const response = await onGetAllCountries();
			setCountries(
				response.data.data.countries.map((el: any) => {
					return { label: el.name, value: el.id };
				}),
			);
			setIsCountriesLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const getLanguages = async () => {
		try {
			const response = await onGetAllLanguages();
			setLanguages(
				response.data.data.languages.map((el) => {
					return { label: el.name, value: el.code };
				}),
			);
			setIsLanguagesLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const handleDateChange: DatePickerProps['onChange'] = (date) => {
		updateForm.setFieldsValue({ date_of_birth: date.set('hours', 6) });
	};

	const handlePhoneChange = ({ dialCode, number, isValid }: PhoneInputReturn) => {
		if (isValid) {
			updateForm.setFieldsValue({
				phone_country_code: dialCode,
				phone_number: number,
			});
		} else {
			updateForm.setFields([
				{
					name: 'phone_number',
					errors: [t('COMMON.INVALID_PHONE')],
				},
			]);
		}
	};

	const onStatusChange = (status: boolean) => {
		updateForm.setFieldsValue({ profile_status: status ? 'Active' : 'Blocked' });
		setUserStatus(status ? 'Active' : 'Blocked');
	};

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const updateUserPayload: Partial<UpdateUserPayload> = {
				name: {
					ar: values.name_ar,
					en: values.name_en,
				},
				national_id: `${values.national_id}`,
				date_of_birth: dayjs(values.date_of_birth).format('YYYY-MM-DD hh:mm:ss'),
				phone_country_code: values.phone_country_code.replace('+', ''),
				phone_number: values.phone_number,
				address: values.address,
				profile_status: values.profile_status,
				locale: values.locale,
				country_id: values.country_id,
				email: values.email,
			};

			if (values.profile_status == 'Blocked') updateUserPayload['profile_status_reason'] = values.profile_status_reason;

			await onUpdateUser(id as string, updateUserPayload);
			NotificationService.success(t('COMMON.SUCCESS'), t('USERS.CLIENT_UPDATED'));
			navigate('/users');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.SUCCESS'), t('USERS.CLIENT_NOT_UPDATED'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('USERS.UPDATE_CLIENT')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className="sectionContainer">
					<h5 style={{ marginBottom: 20 }}>{t('USERS.CLIENT_DETAILS')}</h5>

					<Form name="updateForm" form={updateForm} onFinish={onSubmit}>
						<Row gutter={[20, 20]}>
							{/*a r name input */}
							<Col span={24} md={12}>
								<h6>{t('USERS.NAME_ARABIC')}</h6>
								<Form.Item<FieldType>
									name="name_ar"
									rules={[
										{ required: true, message: t('USERS.AR_NAME_REQUIRED') },
										{ min: 12, message: t('USERS.MIN_12') },
										{ max: 60, message: t('USERS.MAX_60') },
									]}>
									<Input size="large" placeholder={t('USERS.ENTER_NAME_ARABIC')} />
								</Form.Item>
							</Col>

							{/* en name input */}
							<Col span={24} md={12}>
								<h6>{t('USERS.NAME_ENGLISH')}</h6>
								<Form.Item<FieldType>
									name="name_en"
									rules={[
										{ required: true, message: t('USERS.EN_NAME_REQUIRED') },
										{ min: 12, message: t('USERS.MIN_12') },
										{ max: 60, message: t('USERS.MAX_60') },
									]}>
									<Input size="large" placeholder={t('USERS.ENTER_NAME_ENGLISH')} />
								</Form.Item>
							</Col>

							{/* national id input */}
							<Col span={24} md={12}>
								<h6>{t('USERS.NATIONAL_ID')}</h6>
								<Form.Item<FieldType>
									name="national_id"
									rules={[
										{ required: true, message: t('USERS.NATIONAL_ID_REQUIRED') },
										{ len: 10, message: t('USERS.NATIONAL_ID_10') },
									]}>
									<Input size="large" placeholder={t('USERS.ENTER_NATIONAL_ID')} />
								</Form.Item>
							</Col>

							{/* email input */}
							<Col span={24} md={12}>
								<h6>{t('USERS.EMAIL')}</h6>
								<Form.Item<FieldType> name="email" rules={[{ type: 'email', message: t('USERS.EMAIL_NOT_VALID') }]}>
									<Input size="large" placeholder={t('USERS.ENTER_EMAIL')} />
								</Form.Item>
							</Col>

							{/* date of birth input */}
							<Col span={24} md={12}>
								<h6>{t('USERS.DATE_OF_BIRTH')}</h6>
								<Form.Item<FieldType> name="date_of_birth" rules={[{ required: true, message: t('USERS.DATE_BIRTH_REQUIRED') }]}>
									<DatePicker
										size="large"
										inputReadOnly
										style={{ width: '100%' }}
										maxDate={dayjs('30-10-2006', 'DD-MM-YYYY')}
										suffixIcon={<Icon name="calendar" />}
										onChange={handleDateChange}
										placeholder={t('USERS.ENTER_DATE_BIRTH')}
										allowClear={false}
									/>
								</Form.Item>
							</Col>

							{/* phone input */}
							<Col span={24} md={12}>
								<h6>{t('COMMON.PHONE_NUMBER')}</h6>
								<Form.Item<FieldType> name="phone_country_code" hidden>
									<Input hidden />
								</Form.Item>
								<Form.Item<FieldType> name="phone_number">
									<PhoneInputComponent
										onChange={handlePhoneChange}
										userDialCode={user?.phone_country_code}
										userPhoneNumber={user?.phone_number}
									/>
								</Form.Item>
							</Col>

							{/* address input */}
							<Col span={24} md={12}>
								<h6>{t('USERS.ADDRESS')}</h6>
								<Form.Item<FieldType> name="address" rules={[{ required: true, message: t('USERS.CLIENT_ADDRESS_REQUIRED') }]}>
									<Input size="large" placeholder={t('USERS.ENTER_ADDRESS')} />
								</Form.Item>
							</Col>

							{/* locale input */}
							<Col span={24} md={12}>
								<h6>{t('USERS.LOCALE')}</h6>
								<Form.Item<FieldType> name="locale" rules={[{ required: true, message: t('USERS.LOCAL_REQUIRED') }]}>
									<Select style={{ width: '100%' }} size="large" options={languages} loading={isLanguagesLoading} />
								</Form.Item>
							</Col>

							{/* language input */}
							<Col span={24} md={12}>
								<h6>{t('USERS.COUNTRY')}</h6>
								<Form.Item<FieldType> name="country_id" rules={[{ required: true, message: t('USERS.COUNTRY_REQUIRED') }]}>
									<Select style={{ width: '100%' }} size="large" options={countries} loading={isCountriesLoading} />
								</Form.Item>
							</Col>

							<Col span={24}>
								<div className="d-flex gap-2 mb-3">
									<Form.Item<FieldType> name="profile_status" hidden>
										<Input />
									</Form.Item>
									<Switch defaultChecked={updateForm.getFieldValue('profile_status') == 'Active'} onChange={onStatusChange} />
									<p className="mb-0 fw-medium">{t('COMMON.STATUS')}</p>
								</div>

								{userStatus === 'Blocked' && (
									<div>
										<h6>{t('USERS.BLOCK_REASON')}</h6>
										<Form.Item<FieldType>
											name="profile_status_reason"
											rules={[
												{ required: updateForm.getFieldValue('profile_status') == 'Blocked', message: t('USERS.BLOCK_REASON_REQUIRED') },
											]}>
											<TextArea size="large" placeholder={t('USERS.ENTER_REASON')} autoSize={{ minRows: 3, maxRows: 5 }} />
										</Form.Item>
									</div>
								)}
							</Col>
						</Row>
					</Form>

					<Form.Item>
						<Button
							size="middle"
							type="primary"
							htmlType="submit"
							form="updateForm"
							loading={isSubmitting}
							style={{ marginTop: 20, display: 'flex' }}>
							Update
						</Button>
					</Form.Item>
				</div>
			)}
		</>
	);
}

export default UpdateUserPage;
